import { Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Subscription, SubscriptionInterval } from 'src/app/data/schema/cbs/subscription';
import { CommunitySubscription } from 'src/app/data/schema/cbs/community-subscription';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { PaymentCheckout } from 'src/app/data/schema/ecommerce/transaction';
import { CommunityService, SignalREvents } from 'src/app/data/service/community.service';
import { Community } from 'src/app/data/schema/community';

@Component({
    selector: 'app-manage-subscription',
    templateUrl: './manage-subscription.component.html',
    styleUrls: ['./manage-subscription.component.scss'],
})
export class ManageSubscriptionComponent implements OnInit, OnDestroy {

    @ContentChild(TemplateRef) customTemplate: TemplateRef<any>;

    currencyFormat?: string = '1.0-0'; //specify currency format shown for a subscription
    loaded: boolean = false;
    subscriptions: Subscription[] = [];
    selectedInterval: SubscriptionInterval = SubscriptionInterval.Yearly;

    private subscription: any;
    private waitingForSignalR = false;
    private signalrInterval: any;
    private waitLoad: HTMLIonLoadingElement;

    constructor(communityService: CommunityService) {
        this.subscription = communityService.communitySignalrEvent.subscribe(async (communityEvent: any) => {
            // for now, no signalr, just refresh the subscription after a few seconds
            // if (communityEvent.event === SignalREvents.CommunitySubscriptionUpdated) {
            //     this.waitingForSignalR = false;
            //     if (this.signalrInterval != null) {
            //         await this.waitLoad.dismiss();
            //         clearInterval(this.signalrInterval);
            //     }
            // }
        });

    }

    get activeSubscription() { return CommunitySubscription.currentSubscription; }

    ngOnInit() {
        this.load();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public get SubscriptionInterval() {
        return SubscriptionInterval;
    }

    async switchCycle(subscription: Subscription) {
        const confirm = await ServiceHelper.confirmSomething(
            'Change Billing Cycle',
            `Are you sure you want to change your subscriptions billing cycle?<br/><br/>Your subscription will renew with the new cycle at the end of your current term on ${this.activeSubscription.expirationDate}.`, 'Cancel', 'Change');
        if (confirm) {
            await this.subscribe(subscription);
        }
    }

    async upgrade(subscription: Subscription) {
        const confirm = await ServiceHelper.confirmSomething(
            'Upgrade Subscription',
            `Are you sure you want to upgrade your subscription?<br/><br/>Your subscription will be upgraded immediately and you will benefit from a ${subscription.platformFee}% platform fee.<br/><br/>You will immediately be charged the prorated amount for the additional subscription price between now and your next billing cycle.`, 'Cancel', 'Upgrade');
        if (confirm) {
            await this.subscribe(subscription);
        }
    }

    async downgrade(subscription: Subscription) {
        const confirm = await ServiceHelper.confirmSomething(
            'Downgrade Subscription',
            `Are you sure you want to downgrade your subscription?<br/><br/>Your subscription will be downgraded at the end of your current term on ${this.activeSubscription.expirationDate}. Your platform fee will rise to ${subscription.platformFee}% platform fee.`, 'Cancel', 'Downgrade');
        if (confirm) {
            await this.subscribe(subscription);
        }
    }

    async subscribe(subscription: Subscription) {
        const price = subscription.prices.find(p => p.interval == this.selectedInterval);
        try {
            // set that we are modifying the community 
            Community.activeCommunity.didSave = true;
            const isNewSubscription = this.activeSubscription == null;
            if (!isNewSubscription) {
                this.waitingForSignalR = true;
            }
            const checkoutResp = await CommunitySubscription.subscribe(price);
            if (checkoutResp != null) {
                this.openCheckout(checkoutResp);
            } else if (this.activeSubscription != null) {
                await this.refreshSubscription();
                const alert = await ServiceHelper.displayAlert('Subscription Modified', 'Your subscription has been modified.');
                await alert.onDidDismiss();
            }
        } catch (error) {
            this.waitingForSignalR = false;
        }
    }

    async unsubscribe() {
        const confirm = await ServiceHelper.confirmSomething(
            'Cancel Subscription',
            `Are you sure you want to cancel your subscription?<br/><br/>Your subscription will be cancelled at the end of your current term on ${this.activeSubscription.expirationDate}. At which point you will lose access to some features and your transaction fee will increase to 4.9%`, 'Cancel', 'Unsubscribe');
        if (confirm) {
            try {
                this.waitingForSignalR = true;
                Community.activeCommunity.didSave = true;
                await CommunitySubscription.unsubscribe();
                await this.refreshSubscription();
            } catch (e) {
                console.error(e);
                this.waitingForSignalR = false;
            }
        }
    }

    async resume() {
        const confirm = await ServiceHelper.confirmSomething(
            'Resume Subscription',
            `Are you sure you want to resume the automated billing of your current subscription?<br/><br/>Your subscription will be renewed at the end of your current term on ${this.activeSubscription.expirationDate}.`, 'Cancel', 'Resume');
        if (confirm) {
            try {
                this.waitingForSignalR = true;
                Community.activeCommunity.didSave = true;
                await CommunitySubscription.cancelUnsubscribe();
                await this.refreshSubscription();
            } catch (e) {
                console.error(e);
                this.waitingForSignalR = false;
            }
        }
    }

    private async load() {
        this.loaded = false;
        this.subscriptions = await Subscription.loadAll();
        if (this.activeSubscription != null) {
            this.selectedInterval = this.activeSubscription.interval;
        } else {
            this.selectedInterval = SubscriptionInterval.Yearly;
        }
        this.loaded = true;
    }

    private openCheckout(checkout: PaymentCheckout) {
        // first store our redirect url in local storage so we can get it back after the stripe checkout
        // get the current base url for the client
        localStorage.setItem('cancel_url', `${AppSettings.baseUrl}/community/subscription?status=cancelled&purchase_id=${checkout.transactionId}`);
        localStorage.setItem('success_url', `${AppSettings.baseUrl}/community/subscription?status=success`);

        // Then open the stripe checkout window
        window.location.href = checkout.checkoutUrl;
    }

    private async refreshSubscription() {
        return new Promise(async (resolve, reject) => {
            if (!this.waitingForSignalR) {
                return resolve(true);
            }

            // if we haven't gotten a signalR event in 4 seconds, then we need to refresh the subscription
            // we wait a total of 4 seconds because we wait 2 seconds between retries, to give a chance
            // for the signalR event to come in and set waitingForSignalR to false

            let retries = 2;
            this.waitLoad = await ServiceHelper.displayLoad('Refreshing Subscription Details');
            this.signalrInterval = setInterval(async () => {
                if (this.waitingForSignalR === false) {
                    clearInterval(this.signalrInterval);
                    await this.waitLoad.dismiss();
                    resolve(true);
                } else if (retries === 0) {
                    await CommunitySubscription.getCurrent(true, true);
                    this.waitingForSignalR = false;
                    clearInterval(this.signalrInterval);
                    await this.waitLoad.dismiss();
                    resolve(true);
                } else {
                    retries--;
                }
            }, 2000);

        });
    }
}
